import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { suspensionApi, SuspensionRead, SuspensionListParams } from '../../services/apiService';
import { formatToLocalTime } from '../../utils/dateUtils';
import SuspensionFormDialog from './SuspensionFormDialog';
import SuspensionDeleteDialog from './SuspensionDeleteDialog';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
  OutlinedInput,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { subMonths } from 'date-fns';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatSuspensionDate = (dateStr: string): string =>
  formatToLocalTime(dateStr, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });

const twoMonthsAgoISO = (): string => {
  const d = subMonths(new Date(), 2);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** États possibles dans le filtre (étendus avec les sous-types de suppression) */
type StateFilter = 'active' | 'scheduled' | 'expired' | 'stopped' | 'cancelled' | 'interrupted';

const ALL_STATES:  StateFilter[] = ['active', 'scheduled', 'expired', 'stopped', 'cancelled', 'interrupted'];
const PAST_STATES: StateFilter[] = ['expired', 'stopped', 'cancelled', 'interrupted'];

type SortField = 'sensor_name' | 'start_at' | 'state' | 'creator_name' | 'created_at' | 'deleter_name' | 'deleted_at';
type SortDir   = 'asc' | 'desc';

const STATE_COLORS: Record<string, 'success' | 'primary' | 'default' | 'error'> = {
  active:    'success',
  scheduled: 'primary',
  expired:   'default',
  deleted:   'default',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SuspensionList: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Table data
  const [suspensions, setSuspensions] = useState<SuspensionRead[]>([]);
  const [total, setTotal]             = useState(0);
  const [page, setPage]               = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);

  // Filters
  const [searchText,    setSearchText]    = useState('');
  const [stateFilters,  setStateFilters]  = useState<StateFilter[]>([]);
  const [includePast,   setIncludePast]   = useState(false);
  const [twoMonths,     setTwoMonths]     = useState(true);

  // Sort — transmis directement au backend
  const [sortField, setSortField] = useState<SortField>('start_at');
  const [sortDir,   setSortDir]   = useState<SortDir>('asc');

  // Pre-fill sensor_id from URL ?sensor_id=X
  const searchParams    = new URLSearchParams(location.search);
  const sensorIdFromUrl = searchParams.get('sensor_id')
    ? Number(searchParams.get('sensor_id'))
    : undefined;

  // Dialog states
  const [formOpen,      setFormOpen]      = useState(false);
  const [deleteTarget,  setDeleteTarget]  = useState<SuspensionRead | null>(null);
  const [deleting,      setDeleting]      = useState(false);

  // -------------------------------------------------------------------------
  // Data fetching — tri, recherche et pagination délégués au backend
  // -------------------------------------------------------------------------

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const needsPast = includePast || stateFilters.some((f) => PAST_STATES.includes(f));

      const params: SuspensionListParams = {
        page:         page + 1,
        page_size:    rowsPerPage,
        sort_field:   sortField,
        sort_dir:     sortDir,
        include_past: needsPast,
      };

      if (sensorIdFromUrl)      params.sensor_id = sensorIdFromUrl;
      if (twoMonths)            params.date_from  = twoMonthsAgoISO();
      if (searchText.trim())    params.search     = searchText.trim();

      const response = await suspensionApi.list(params);
      setSuspensions(response.data.items);
      setTotal(response.data.total);
    } catch (err: any) {
      setError(err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortField, sortDir, stateFilters, includePast, twoMonths, searchText, sensorIdFromUrl, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await suspensionApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setError(err.message || t('common.error'));
    } finally {
      setDeleting(false);
    }
  };

  /** Change les filtres état ET synchronise la case "Inclure échu" */
  const handleStateFiltersChange = (next: StateFilter[]) => {
    setStateFilters(next);
    setPage(0);
    const allPastChecked = PAST_STATES.every((s) => next.includes(s));
    setIncludePast(allPastChecked);
  };

  /** Change "Inclure échu" ET synchronise les états passés dans le filtre */
  const handleIncludePastChange = (checked: boolean) => {
    setIncludePast(checked);
    setPage(0);
    if (checked) {
      setStateFilters((prev) => [
        ...prev.filter((s) => !PAST_STATES.includes(s)),
        ...PAST_STATES,
      ]);
    } else {
      setStateFilters((prev) => prev.filter((s) => !PAST_STATES.includes(s)));
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(0);
  };

  // -------------------------------------------------------------------------
  // Render helpers
  // -------------------------------------------------------------------------

  const renderStateChip = (susp: SuspensionRead) => {
    if (susp.state === 'deleted' && susp.deletion_type) {
      return (
        <Chip
          label={t(`suspensions.deletionTypes.${susp.deletion_type}`)}
          color="default"
          size="small"
        />
      );
    }
    return (
      <Chip
        label={t(`suspensions.states.${susp.state}`)}
        color={STATE_COLORS[susp.state] ?? 'default'}
        size="small"
      />
    );
  };

  /** Tooltip adaptatif sur le bouton poubelle selon l'état */
  const deleteTooltip = (susp: SuspensionRead): string => {
    if (susp.state === 'scheduled') return t('suspensions.delete.actionCancel') as string;
    if (susp.state === 'active') {
      return (susp.is_indefinite
        ? t('suspensions.delete.actionStop')
        : t('suspensions.delete.actionInterrupt')) as string;
    }
    return t('suspensions.delete.openDialog') as string;
  };

  const SortableCell: React.FC<{ field: SortField; label: string }> = ({ field, label }) => (
    <TableCell sortDirection={sortField === field ? sortDir : false}>
      <TableSortLabel
        active={sortField === field}
        direction={sortField === field ? sortDir : 'asc'}
        onClick={() => handleSort(field)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const colSpan = isAdmin ? 8 : 7;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <PauseCircleOutlineIcon color="action" />
          <Typography variant="h5" component="h1">
            {t('suspensions.title')}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title={t('common.refresh')}>
            <IconButton onClick={fetchData} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setFormOpen(true)}
              size="small"
            >
              {t('suspensions.newButton')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack spacing={2}>
          {/* Ligne 1 : recherche + filtre état */}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              size="small"
              placeholder={t('suspensions.filters.search') as string}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>{t('suspensions.columns.state')}</InputLabel>
              <Select
                multiple
                value={stateFilters}
                input={<OutlinedInput label={t('suspensions.columns.state')} />}
                onChange={(e) => {
                  const raw = e.target.value as string[];
                  if (raw.includes('__all__')) {
                    // "Tous" : bascule tout coché / tout décoché
                    handleStateFiltersChange(
                      stateFilters.length === ALL_STATES.length ? [] : ALL_STATES
                    );
                  } else {
                    handleStateFiltersChange(raw as StateFilter[]);
                  }
                }}
                renderValue={(selected) =>
                  selected
                    .map((v) =>
                      v !== 'expired' && PAST_STATES.includes(v as StateFilter)
                        ? t(`suspensions.deletionTypes.${v}`)
                        : t(`suspensions.states.${v}`)
                    )
                    .join(', ')
                }
              >
                {/* Option "Tous" */}
                <MenuItem value="__all__" dense>
                  <Checkbox
                    checked={stateFilters.length === ALL_STATES.length}
                    indeterminate={stateFilters.length > 0 && stateFilters.length < ALL_STATES.length}
                    size="small"
                    sx={{ py: 0 }}
                  />
                  <ListItemText primary={<em>{t('common.all')}</em>} />
                </MenuItem>

                {(
                  [
                    { value: 'active',      label: t('suspensions.states.active') },
                    { value: 'scheduled',   label: t('suspensions.states.scheduled') },
                    { value: 'expired',     label: t('suspensions.states.expired') },
                    { value: 'stopped',     label: t('suspensions.deletionTypes.stopped') },
                    { value: 'cancelled',   label: t('suspensions.deletionTypes.cancelled') },
                    { value: 'interrupted', label: t('suspensions.deletionTypes.interrupted') },
                  ] as { value: StateFilter; label: string }[]
                ).map(({ value, label }) => (
                  <MenuItem key={value} value={value} dense>
                    <Checkbox
                      checked={stateFilters.includes(value)}
                      size="small"
                      sx={{ py: 0 }}
                    />
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {/* Ligne 2 : cases à cocher */}
          <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  checked={twoMonths}
                  onChange={(e) => { setTwoMonths(e.target.checked); setPage(0); }}
                  size="small"
                />
              }
              label={t('suspensions.filters.twoMonths')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includePast}
                  onChange={(e) => handleIncludePastChange(e.target.checked)}
                  size="small"
                />
              }
              label={t('suspensions.filters.includePast')}
            />
          </Stack>
        </Stack>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <SortableCell field="sensor_name"  label={t('suspensions.columns.sensor')} />
              <SortableCell field="start_at"     label={t('suspensions.columns.range')} />
              <SortableCell field="state"        label={t('suspensions.columns.state')} />
              <SortableCell field="creator_name" label={t('suspensions.columns.createdBy')} />
              <SortableCell field="created_at"   label={t('suspensions.columns.createdAt')} />
              <SortableCell field="deleter_name" label={t('suspensions.columns.stoppedBy')} />
              <SortableCell field="deleted_at"   label={t('suspensions.columns.stoppedAt')} />
              {isAdmin && <TableCell align="right">{t('suspensions.columns.actions')}</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : suspensions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{t('common.noData')}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              suspensions.map((susp) => (
                <TableRow
                  key={susp.id}
                  sx={{
                    opacity: susp.state === 'deleted' || susp.state === 'expired' ? 0.55 : 1,
                  }}
                >
                  {/* Capteur */}
                  <TableCell>
                    {susp.sensor_name ? (
                      <RouterLink
                        to={`/sensors/${susp.sensor_id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{ '&:hover': { textDecoration: 'underline' } }}
                        >
                          {susp.sensor_name}
                        </Typography>
                      </RouterLink>
                    ) : (
                      `#${susp.sensor_id}`
                    )}
                  </TableCell>

                  {/* Plage */}
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {formatSuspensionDate(susp.start_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      →{' '}
                      {susp.is_indefinite ? (
                        <strong>{t('suspensions.indefinite')}</strong>
                      ) : (
                        formatSuspensionDate(susp.end_at)
                      )}
                    </Typography>
                  </TableCell>

                  {/* État */}
                  <TableCell>{renderStateChip(susp)}</TableCell>

                  {/* Créée par */}
                  <TableCell>
                    <Typography variant="body2">
                      {susp.creator_name ?? `#${susp.created_by}`}
                    </Typography>
                  </TableCell>

                  {/* Créée le */}
                  <TableCell>
                    <Typography variant="body2">{formatSuspensionDate(susp.created_at)}</Typography>
                  </TableCell>

                  {/* Arrêtée/Supprimée par */}
                  <TableCell>
                    <Typography variant="body2">
                      {susp.deleter_name ?? (susp.deleted_by ? `#${susp.deleted_by}` : '—')}
                    </Typography>
                  </TableCell>

                  {/* Arrêtée/Supprimée le */}
                  <TableCell>
                    <Typography variant="body2">
                      {susp.deleted_at ? formatSuspensionDate(susp.deleted_at) : '—'}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  {isAdmin && (
                    <TableCell align="right">
                      {susp.state !== 'deleted' && (
                        <Tooltip
                          title={
                            susp.state === 'expired'
                              ? t('suspensions.delete.disabledExpired')
                              : deleteTooltip(susp)
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(susp)}
                              disabled={susp.state === 'expired'}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={t('pagination.rowsPerPage')}
        />
      </TableContainer>

      {/* Formulaire de création */}
      {formOpen && (
        <SuspensionFormDialog
          open={formOpen}
          onClose={() => setFormOpen(false)}
          onSuccess={() => { setFormOpen(false); fetchData(); }}
          preselectedSensorId={sensorIdFromUrl}
        />
      )}

      {/* Dialog de confirmation de suppression */}
      <SuspensionDeleteDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleting}
        suspension={deleteTarget}
      />
    </Box>
  );
};

export default SuspensionList;
